import { useMemo } from "react";

import {
  Invitation,
  InvitationOfType,
  UseInvitationOptions,
} from "simplydo/interfaces";

import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";
import { useGroupInvitation } from "./useGroupInvitation";
import { useIdeaAssessorInvitation } from "./useIdeaAssessorInvitation";
import { useIdeaBusinessProfileInvitation } from "./useIdeaBusinessProfileInvitation";
import { useIdeaInvitation } from "./useIdeaInvitation";
import { useProjectInvitedInvitation } from "./useProjectInvitedInvitation";
import { useRequestJoinGroupInvitation } from "./useRequestJoinGroupInvitation";
import { useRequestJoinIdeaInvitation } from "./useRequestJoinIdeaInvitation";

export const useInvitation = (
  invitation: Invitation,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const groupInvitation = useGroupInvitation(
    invitation.forType === "group"
      ? (invitation as InvitationOfType<"group">)
      : undefined,
    options,
  );
  const requestJoinGroupInvitation = useRequestJoinGroupInvitation(
    invitation.forType === "requestJoinGroup"
      ? (invitation as InvitationOfType<"requestJoinGroup">)
      : undefined,
    options,
  );
  const ideaInvitation = useIdeaInvitation(
    invitation.forType === "idea"
      ? (invitation as InvitationOfType<"idea">)
      : undefined,
    options,
  );
  const requestJoinIdeaInvitation = useRequestJoinIdeaInvitation(
    invitation.forType === "requestJoinIdea"
      ? (invitation as InvitationOfType<"requestJoinIdea">)
      : undefined,
    options,
  );
  const projectInvitedInvitation = useProjectInvitedInvitation(
    invitation.forType === "projectInvited"
      ? (invitation as InvitationOfType<"projectInvited">)
      : undefined,
    options,
  );
  const ideaBusinessProfileInvitation = useIdeaBusinessProfileInvitation(
    invitation.forType === "ideaBusinessProfile"
      ? (invitation as InvitationOfType<"ideaBusinessProfile">)
      : undefined,
    options,
  );
  const ideaAssessorInvitation = useIdeaAssessorInvitation(
    invitation.forType === "ideaAssessor"
      ? (invitation as InvitationOfType<"ideaAssessor">)
      : undefined,
    options,
  );

  return useMemo(() => {
    switch (invitation.forType) {
      case "group":
        return groupInvitation;
      case "requestJoinGroup":
        return requestJoinGroupInvitation;
      case "idea":
        return ideaInvitation;
      case "requestJoinIdea":
        return requestJoinIdeaInvitation;
      case "projectInvited":
        return projectInvitedInvitation;
      case "ideaBusinessProfile":
        return ideaBusinessProfileInvitation;
      case "ideaAssessor":
        return ideaAssessorInvitation;
      default:
        return emptyInvitationViewModel();
    }
  }, [
    groupInvitation,
    ideaAssessorInvitation,
    ideaBusinessProfileInvitation,
    ideaInvitation,
    invitation.forType,
    projectInvitedInvitation,
    requestJoinGroupInvitation,
    requestJoinIdeaInvitation,
  ]);
};
